import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Loader2, PlugZap } from 'lucide-react';
import { api } from '../lib/api';
import { PageHeader, Button, Field, Input, Select, EmptyState } from '../components/ui';
import { FormError } from '../components/forms/common';

type Status = 'CONNECTED' | 'NOT_CONFIGURED' | 'NOT_IMPLEMENTED';
interface FieldView { key: string; label: string; type: string; options?: Array<{ value: string; label: string }>; hint?: string; value: string | null; set?: boolean }
interface CardView { key: string; label: string; carries: string; status: Status; statusNote: string; fields: FieldView[]; canTest: boolean; editable: boolean; updatedAt: string | null; updatedByName: string | null }
interface TestResult { ok: boolean; sent: boolean; message: string; problems: string[] }

const STATUS: Record<Status, { label: string; colour: string }> = {
  CONNECTED: { label: 'Connected', colour: 'var(--status-good)' },
  NOT_CONFIGURED: { label: 'Not configured', colour: 'var(--status-neutral)' },
  NOT_IMPLEMENTED: { label: 'NOT IMPLEMENTED — requires external credential', colour: 'var(--status-warning)' },
};

/**
 * Settings → Integrations (prototype §32 "Going live"): mail, text messages, the portals, the deposit scheme, the bank
 * feed and the assistant's key. Secrets are never shown — only whether one is set. Each card says honestly where it
 * stands, can be tested, and shows exactly what it would send.
 */
export default function IntegrationsPage() {
  const q = useQuery({ queryKey: ['/integrations'], queryFn: async () => (await api.get<{ cards: CardView[]; outbox: { queued: number; failed: number } }>('/integrations')).data });
  return (
    <div>
      <PageHeader title="Integrations" />
      {q.isLoading ? <div className="surface h-64 animate-pulse" /> : q.error ? <EmptyState icon="TriangleAlert" title="Could not load the integrations" body={(q.error as Error).message} /> : (
        <>
          <div className="surface mb-2.5 flex flex-wrap items-center gap-2 px-4 py-2.5 text-[12.5px] text-ink-600">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-500">Right now</span>
            <span className="rounded-full bg-ink-100 px-2.5 py-0.5">{q.data!.cards.filter((c) => c.status === 'CONNECTED').length} connected</span>
            <span className="rounded-full bg-ink-100 px-2.5 py-0.5">{q.data!.outbox.queued} waiting in the outbox</span>
            {q.data!.outbox.failed > 0 && <span className="rounded-full bg-red-50 px-2.5 py-0.5 text-red-700">{q.data!.outbox.failed} failed</span>}
          </div>
          <div className="grid gap-2.5 lg:grid-cols-2">{q.data!.cards.map((c) => <IntegrationCard key={c.key} card={c} />)}</div>
        </>
      )}
    </div>
  );
}

function IntegrationCard({ card }: { card: CardView }) {
  const qc = useQueryClient();
  const initial = Object.fromEntries(card.fields.filter((f) => f.type !== 'secret').map((f) => [f.key, f.value ?? '']));
  const [values, setValues] = useState<Record<string, string>>(initial);
  const [secrets, setSecrets] = useState<Record<string, string | null>>({});
  const [peek, setPeek] = useState(false);
  const [result, setResult] = useState<TestResult | null>(null);
  const dirty = card.fields.some((f) => f.type !== 'secret' && (values[f.key] ?? '') !== (f.value ?? '')) || Object.keys(secrets).length > 0;
  const st = STATUS[card.status];
  const real = card.key !== 'anthropic';

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, string | null> = {};
      for (const f of card.fields) {
        if (f.type === 'secret') { if (f.key in secrets) body[f.key] = secrets[f.key] ?? null; }
        else if ((values[f.key] ?? '') !== (f.value ?? '')) body[f.key] = values[f.key] || null;
      }
      return (await api.patch<CardView>(`/integrations/${card.key}`, body)).data;
    },
    onSuccess: () => { setSecrets({}); setResult(null); void qc.invalidateQueries({ queryKey: ['/integrations'] }); },
  });
  const test = useMutation({ mutationFn: async () => (await api.post<TestResult>(`/integrations/${card.key}/test`, {})).data, onSuccess: setResult });
  const payload = useQuery({ queryKey: ['/integrations', card.key, 'payload'], enabled: peek && real, queryFn: async () => (await api.get<unknown>(`/integrations/${card.key}/payload`)).data });

  return (
    <div className="surface flex flex-col p-4">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <PlugZap className="h-4 w-4 text-bronze-500" />
        <h2 className="flex-1 text-[13px] font-semibold text-ink-900">{card.label}</h2>
        <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-[3px] text-[11px] font-medium" style={{ color: st.colour, background: `color-mix(in srgb, ${st.colour} 12%, var(--color-white))` }}>
          <i className="h-1.5 w-1.5 rounded-full" style={{ background: st.colour }} />{st.label}
        </span>
      </div>
      <p className="text-[12.5px] text-ink-600">{card.carries}</p>
      <p className="mt-1 text-[11.5px] text-ink-400">{card.statusNote}</p>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {card.fields.map((f) => (
          <Field key={f.key} label={f.label} hint={f.hint}>
            {f.type === 'select' ? (
              <Select value={values[f.key] ?? ''} disabled={!card.editable} onChange={(e) => setValues({ ...values, [f.key]: e.target.value })} className="w-full">
                {f.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </Select>
            ) : f.type === 'secret' ? (
              <div className="flex items-center gap-1.5">
                <Input type="password" autoComplete="new-password" disabled={!card.editable} className="w-full"
                  value={secrets[f.key] ?? ''} onChange={(e) => setSecrets({ ...secrets, [f.key]: e.target.value })}
                  placeholder={secrets[f.key] === null ? 'will be removed' : f.set ? '•••••••• set — type to replace' : 'not set'} />
                {card.editable && f.set && !(f.key in secrets) && <button onClick={() => setSecrets({ ...secrets, [f.key]: null })} className="shrink-0 text-[11.5px] text-red-600 hover:underline">Remove</button>}
              </div>
            ) : (
              <Input value={values[f.key] ?? ''} disabled={!card.editable} onChange={(e) => setValues({ ...values, [f.key]: e.target.value })} className="w-full" type={f.type === 'email' ? 'email' : f.type === 'phone' ? 'tel' : 'text'} />
            )}
          </Field>
        ))}
      </div>

      {real && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {card.editable && <Button size="sm" onClick={() => save.mutate()} disabled={!dirty || save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</Button>}
          {card.editable && dirty && <Button size="sm" variant="ghost" onClick={() => { setValues(initial); setSecrets({}); }}>Discard</Button>}
          {card.editable && card.canTest && <Button size="sm" variant="secondary" icon="FlaskConical" onClick={() => test.mutate()} disabled={test.isPending || dirty} title={dirty ? 'Save first' : ''}>{card.key === 'mail' ? 'Send me a test' : 'Check the fields'}</Button>}
          <div className="flex-1" />
          {card.updatedAt && <span className="text-[11px] text-ink-400">Saved {new Date(card.updatedAt).toLocaleDateString('en-GB')}{card.updatedByName ? ` by ${card.updatedByName}` : ''}</span>}
        </div>
      )}
      {test.isPending && <p className="mt-2 flex items-center gap-1 text-[12px] text-ink-500"><Loader2 className="h-3.5 w-3.5 animate-spin" />Testing…</p>}
      {result && (
        <div className={`mt-2 rounded-lg border px-3 py-2 text-[12px] ${result.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
          {result.message}{result.problems.length > 0 && <ul className="mt-1 list-disc pl-4">{result.problems.map((p) => <li key={p}>{p}</li>)}</ul>}
        </div>
      )}
      {(save.error || test.error) && <div className="mt-2"><FormError error={save.error ?? test.error} /></div>}

      {real && (
        <div className="mt-3 rounded-lg border border-ink-200">
          <button onClick={() => setPeek((v) => !v)} className="flex w-full items-center justify-between px-3.5 py-2 text-[12.5px] font-medium text-ink-700">
            What it carries <ChevronDown className={`h-4 w-4 transition ${peek ? 'rotate-180' : ''}`} />
          </button>
          {peek && (
            <div className="border-t border-ink-100 px-3.5 py-3">
              {payload.isLoading ? <div className="h-24 animate-pulse rounded bg-ink-50" /> : payload.error ? <FormError error={payload.error} /> : (
                <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-all rounded-lg bg-ink-50 p-3 font-mono text-[11px] leading-relaxed text-ink-800">{JSON.stringify(payload.data, null, 2)}</pre>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
